/**
 * Pagination pure, partagée par toutes les listes de l'app et du site.
 * Page 1-indexée ; une page hors bornes est ramenée dans [1, pageCount].
 */

export const DEFAULT_PAGE_SIZE = 20;

export interface PageSlice<T> {
  items: T[];
  page: number;
  pageCount: number;
  total: number;
  /** Index (1-based) du 1er et du dernier élément affichés ; 0 si vide. */
  from: number;
  to: number;
}

export function pageCount(total: number, pageSize = DEFAULT_PAGE_SIZE): number {
  if (!Number.isFinite(total) || total <= 0) return 1;
  return Math.max(1, Math.ceil(total / Math.max(1, Math.floor(pageSize))));
}

export function clampPage(page: number, count: number): number {
  if (!Number.isFinite(page)) return 1;
  return Math.min(Math.max(1, Math.floor(page)), Math.max(1, count));
}

export function paginate<T>(
  items: readonly T[],
  page: number,
  pageSize = DEFAULT_PAGE_SIZE,
): PageSlice<T> {
  const size = Math.max(1, Math.floor(pageSize));
  const count = pageCount(items.length, size);
  const p = clampPage(page, count);
  const start = (p - 1) * size;
  const slice = items.slice(start, start + size);
  return {
    items: slice,
    page: p,
    pageCount: count,
    total: items.length,
    from: slice.length ? start + 1 : 0,
    to: start + slice.length,
  };
}

/**
 * Numéros de page à afficher : toujours la 1re et la dernière, la page
 * courante ± `around`, et `null` pour une ellipse. Ex. (7, 20) → 1 … 6 7 8 … 20.
 */
export function pageWindow(page: number, count: number, around = 1): (number | null)[] {
  const p = clampPage(page, count);
  const keep = new Set<number>([1, count]);
  for (let i = p - around; i <= p + around; i++) if (i >= 1 && i <= count) keep.add(i);
  const sorted = [...keep].sort((a, b) => a - b);
  const out: (number | null)[] = [];
  sorted.forEach((n, i) => {
    if (i > 0 && n - sorted[i - 1]! > 1) out.push(null);
    out.push(n);
  });
  return out;
}
